import { InputActions, Layer, ZIndexes } from "@common/constants";
import {
    encodeTrainingCommand,
    TrainingCommand,
    trainingDummySpots,
    TrainingLayout,
    TrainingLoadout,
    trainingSlotAt,
    trainingSlotOrigin
} from "@common/definitions/training";
import { DefinitionType } from "@common/utils/objectDefinitions";
import { type Vector } from "@common/utils/vector";
import { Container, Graphics, Text } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { escapeText } from "../utils/misc";
import { canvasLine, spacedLetters } from "../utils/translations/languageLook";
import { t } from "../utils/translations/phrases";
import { selectedLanguageCode } from "../utils/translations/translations";
import { CameraManager } from "./cameraManager";
import { InputManager } from "./inputManager";
import { MapManager } from "./mapManager";
import { UIManager } from "./uiManager";

// v2: the Gravity Gloves and their yard were added (the welcome shows once more)
const HELP_SEEN_KEY = "trenches_training_help_seen_v2";
/** the dummies' armour, by level (in the language picked) */
const armorNames = (): string[] => [t("No armour"), t("Level <level>", { level: 1 }), t("Level <level>", { level: 2 }), t("Level <level>", { level: 3 })];

/**
 * The Training Range on the client: a stats strip (last hit, DPS, damage, accuracy, time to kill), a settings
 * panel (dummy armour, still or strafing dummies, reset, exit), first-visit help, and lane / rack markings drawn
 * on the ground of the player's range. Stats are worked out here from the hits the server reports and the
 * shots the player fires, so nothing extra is sent over the network.
 */
class TrainingHudClass {
    private _active = false;
    private _root?: HTMLElement;
    private _panel?: HTMLElement;
    private _overlay?: Container;
    private _slot = -1;

    private readonly _recent: Array<{ readonly t: number, readonly amount: number }> = [];
    private _total = 0;
    private _hits = 0;
    private _shots = 0;
    private _last = 0;
    private _lastDistance = 0;
    private _weapon = "";
    private _magazine = -1;
    private _armor = 0;
    private _moving = false;
    private _nextRefresh = 0;
    private readonly _onKey = (event: KeyboardEvent): void => {
        if (event.key === "F3") {
            event.preventDefault();
            this.togglePanel();
        } else if (event.key === "Escape" && this._panel && !this._panel.hidden) {
            this._panel.hidden = true;
        }
    };

    get active(): boolean { return this._active; }

    enter(): void {
        // every visit is a new training round: nothing carries over, even if the bar never closed after the last one
        if (this._active) window.removeEventListener("keydown", this._onKey, true);
        this._active = true;
        this._build();
        this._resetStats();
        this._armor = 0;
        this._moving = false;
        this._syncPanel();
        document.body.classList.add("training");
        this._root!.hidden = false;
        MapManager.visible = false;
        window.addEventListener("keydown", this._onKey, true);
        const help = this._root!.querySelector<HTMLElement>("#training-help")!;
        let seen = false;
        try { seen = localStorage.getItem(HELP_SEEN_KEY) === "1"; } catch { /* storage blocked */ }
        help.hidden = seen;
    }

    leave(): void {
        if (!this._active) return;
        this._active = false;
        document.body.classList.remove("training");
        if (this._root) this._root.hidden = true;
        if (this._panel) this._panel.hidden = true;
        this._overlay?.destroy({ children: true });
        this._overlay = undefined;
        this._slot = -1;
        // (as the settings have it: hidden, it stays so)
        MapManager.visible = !GameConsole.getBuiltInCVar("cv_minimap_minimized") && GameConsole.getBuiltInCVar("cv_draw_hud");
        window.removeEventListener("keydown", this._onKey, true);
    }

    /** a hit the server reported (damage after armour) on something at this position */
    onHit(amount: number, position?: Vector): void {
        if (!this._active || !(amount > 0)) return;
        const now = performance.now();
        this._recent.push({ t: now, amount });
        this._total += amount;
        this._hits++;
        this._last = amount;
        const me = Game.activePlayer?.position;
        if (me && position) this._lastDistance = Math.hypot(position.x - me.x, position.y - me.y);
    }

    update(): void {
        if (!this._active) return;
        this._drawRangeMarkings();
        this._countShots();
        const now = performance.now();
        if (now < this._nextRefresh) return;
        this._nextRefresh = now + 120;
        this._render(now);
    }

    togglePanel(): void {
        if (!this._panel) return;
        this._panel.hidden = !this._panel.hidden;
        if (!this._panel.hidden) this._syncPanel();
    }

    private _send(command: TrainingCommand, value = 0): void {
        InputManager.addAction({ type: InputActions.TrainingCommand, command: encodeTrainingCommand(command, value) });
    }

    private _resetStats(): void {
        this._recent.length = 0;
        this._total = this._hits = this._shots = this._last = this._lastDistance = 0;
        this._weapon = "";
        this._magazine = -1;
    }

    /** shots fired: every drop in the active gun's magazine (reloads go up, switching guns starts over) */
    private _countShots(): void {
        const weapon = UIManager.inventory.weapons[UIManager.inventory.activeWeaponIndex];
        const id = weapon?.definition.idString ?? "";
        const count = weapon?.definition.defType === DefinitionType.Gun ? weapon.count ?? -1 : -1;
        if (id !== this._weapon) {
            this._weapon = id;
            this._magazine = count;
            return;
        }
        if (count >= 0 && this._magazine >= 0 && count < this._magazine) this._shots += this._magazine - count;
        this._magazine = count;
    }

    private _render(now: number): void {
        while (this._recent.length && now - this._recent[0].t > 3000) this._recent.shift();
        const windowDamage = this._recent.reduce((sum, hit) => sum + hit.amount, 0);
        const set = (id: string, text: string): void => {
            const el = this._root!.querySelector(`#${id}`);
            if (el && el.textContent !== text) el.textContent = text;
        };
        set("ts-last", this._last ? `${Math.round(this._last)}${this._lastDistance ? ` @ ${Math.round(this._lastDistance)} m` : ""}` : "–");
        set("ts-dps", Math.round(windowDamage / 3).toString());
        set("ts-total", Math.round(this._total).toString());
        set("ts-acc", this._shots > 0 ? `${Math.min(100, Math.round(this._hits / this._shots * 100))}%` : "–");

        // time to kill a 100 HP player at the accuracy you're actually getting, with this gun's fire rate
        const weapon = UIManager.inventory.weapons[UIManager.inventory.activeWeaponIndex];
        let ttk = "–";
        if (weapon?.definition.defType === DefinitionType.Gun && this._shots >= 3 && this._total > 0) {
            const perShot = this._total / this._shots;
            const shots = Math.ceil(100 / perShot);
            const seconds = (shots - 1) * weapon.definition.fireDelay / 1000;
            ttk = shots > 60 ? "–"
                : shots === 1 ? t("<seconds> s · <shots> shot", { seconds: seconds.toFixed(2), shots })
                    : t("<seconds> s · <shots> shots", { seconds: seconds.toFixed(2), shots });
        }
        set("ts-ttk", ttk);
        set("ts-setup", t("Dummies: <armour> · <movement>", { armour: armorNames()[this._armor].toLowerCase(), movement: this._moving ? t("strafing") : t("still") }));
    }

    private _syncPanel(): void {
        if (!this._panel) return;
        this._panel.querySelectorAll<HTMLButtonElement>("[data-armor]").forEach(button => button.classList.toggle("on", Number(button.dataset.armor) === this._armor));
        this._panel.querySelectorAll<HTMLButtonElement>("[data-moving]").forEach(button => button.classList.toggle("on", (button.dataset.moving === "1") === this._moving));
    }

    private _build(): void {
        if (this._root) return;
        const root = document.createElement("div");
        root.id = "training-hud";
        root.hidden = true;
        root.innerHTML = `
            <div id="training-strip">
                <div class="ts-head"><span class="ts-title">${t("Training Range")}</span><span class="ts-setup" id="ts-setup"></span></div>
                <div class="ts-cells">
                    <div class="ts-cell"><b id="ts-last">–</b><span>${t("Last hit")}</span></div>
                    <div class="ts-cell"><b id="ts-dps">0</b><span>${t("DPS · 3 s")}</span></div>
                    <div class="ts-cell"><b id="ts-total">0</b><span>${t("Damage")}</span></div>
                    <div class="ts-cell"><b id="ts-acc">–</b><span>${t("Accuracy")}</span></div>
                    <div class="ts-cell wide"><b id="ts-ttk">–</b><span>${t("Time to kill · 100 HP")}</span></div>
                </div>
                <div class="ts-actions">
                    <button type="button" id="ts-settings" title="${escapeText(t("Dummy armour, strafing dummies"))}"><kbd>F3</kbd> ${t("Range settings")}</button>
                    <button type="button" id="ts-game-settings" title="${escapeText(t("Controls, graphics and sound"))}">${t("Game settings")}</button>
                    <button type="button" id="ts-exit">${t("Exit")}</button>
                    <button type="button" id="ts-reset-settings" title="${escapeText(t("Dummies back to no armour, standing still"))}">${t("Reset settings")}</button>
                    <button type="button" id="ts-reset-stats" title="${escapeText(t("Clear the numbers on this bar"))}">${t("Reset stats")}</button>
                    <button type="button" id="ts-reset-room" title="${escapeText(t("Every rack item back in its place, the floor cleared (loot, fire, gas, smoke, grenades), dummies home"))}">${t("Reset room")}</button>
                </div>
            </div>
            <div id="training-panel" hidden>
                <div class="tp-title">${t("Range settings")}</div>
                <div class="tp-row"><span>${t("Dummy armour")}</span><div class="tp-choices">
                    ${armorNames().map((name, i) => `<button type="button" data-armor="${i}">${name}</button>`).join("")}
                </div></div>
                <div class="tp-row"><span>${t("Dummies")}</span><div class="tp-choices">
                    <button type="button" data-moving="0">${t("Still")}</button><button type="button" data-moving="1">${t("Strafing")}</button>
                </div></div>
                <div class="tp-row tp-buttons">
                    <button type="button" id="tp-reset">${t("Reset room")}</button>
                    <button type="button" id="tp-reset-settings">${t("Default settings")}</button>
                    <button type="button" id="tp-close">${t("Close")}</button>
                    <button type="button" id="tp-exit" class="danger">${t("Exit training")}</button>
                </div>
                <div class="tp-note">${t("Armour on the dummies works exactly as in a real fight.")}</div>
            </div>
            <div id="training-help" hidden>
                <div class="th-help-title">${t("Welcome to the range")}</div>
                <p>${t("Every weapon that drops on Fargo is on the racks: walk over and press <kbd>F</kbd> to take one. Ammo and grenades never run out.")}</p>
                <p>${t("Shoot the dummies: they show every hit and can't die. Your stats are at the top. You can't be hurt here, so try Molotovs and grenades freely.")}</p>
                <p>${t("New: the <b>Gravity Gloves</b> are on the kit rack, with a yard of barrels and boxes below the lane. Hold attack to grab one, the mouse wheel pushes it out or pulls it in, let go (or right-click) to throw it at the dummies, and <kbd>V</kbd> merges two that touch.")}</p>
                <p>${t("<kbd>F3</kbd> opens the settings (dummy armour, strafing dummies). Made a mess? <b>Reset room</b> at the top puts every rack item back and clears the floor; <b>Reset settings</b> puts the dummies back to no armour, standing still. This range is yours alone: nobody else can see it or touch it.")}</p>
                <button type="button" id="th-help-ok">${t("Got it")}</button>
            </div>`;
        // its own layer on the page, above the full-screen joystick layer (clicks pass through everywhere else)
        document.body.appendChild(root);
        // a clicked button gives the keyboard back to the game (focused, it took every key: the game ignored them all, a
        // key held during the click stayed held, and Space pressed "Reset room" again)
        root.addEventListener("click", event => (event.target as HTMLElement | null)?.closest("button")?.blur());
        this._root = root;
        this._panel = root.querySelector<HTMLElement>("#training-panel")!;

        root.querySelector("#ts-settings")!.addEventListener("click", () => this.togglePanel());
        root.querySelector("#ts-reset-stats")!.addEventListener("click", () => this._resetStats());
        const exit = (): void => { void Game.endGame(); };
        root.querySelector("#ts-exit")!.addEventListener("click", exit);
        root.querySelector("#tp-exit")!.addEventListener("click", exit);
        root.querySelector("#tp-close")!.addEventListener("click", () => { this._panel!.hidden = true; });
        // the room as new (racks restocked, floor cleared, dummies home)
        const resetRoom = (): void => { this._send(TrainingCommand.Reset); };
        // the game's own settings (controls, graphics, sound), as from the in-game menu
        root.querySelector("#ts-game-settings")!.addEventListener("click", () => (document.getElementById("btn-settings-game") as HTMLButtonElement | null)?.click());
        root.querySelector("#ts-reset-room")!.addEventListener("click", resetRoom);
        root.querySelector("#tp-reset")!.addEventListener("click", resetRoom);
        // the dummy settings back to the defaults
        const resetSettings = (): void => {
            this._armor = 0;
            this._moving = false;
            this._send(TrainingCommand.ResetSettings);
            this._syncPanel();
            this._render(performance.now());
        };
        root.querySelector("#ts-reset-settings")!.addEventListener("click", resetSettings);
        root.querySelector("#tp-reset-settings")!.addEventListener("click", resetSettings);
        this._panel.querySelectorAll<HTMLButtonElement>("[data-armor]").forEach(button => button.addEventListener("click", () => {
            this._armor = Number(button.dataset.armor);
            this._send(TrainingCommand.DummyArmor, this._armor);
            this._syncPanel();
        }));
        this._panel.querySelectorAll<HTMLButtonElement>("[data-moving]").forEach(button => button.addEventListener("click", () => {
            this._moving = button.dataset.moving === "1";
            this._send(TrainingCommand.DummyMoving, this._moving ? 1 : 0);
            this._syncPanel();
        }));
        root.querySelector("#th-help-ok")!.addEventListener("click", () => {
            try { localStorage.setItem(HELP_SEEN_KEY, "1"); } catch { /* storage blocked */ }
            root.querySelector<HTMLElement>("#training-help")!.hidden = true;
        });
        // clicks on the HUD shouldn't shoot
        for (const el of [root.querySelector("#training-strip"), this._panel, root.querySelector("#training-help")]) {
            el?.addEventListener("pointerdown", event => event.stopPropagation());
            el?.addEventListener("mousedown", event => event.stopPropagation());
        }
    }

    /** lane lines, distance marks and rack labels on the ground of the player's own range (drawn once) */
    private _drawRangeMarkings(): void {
        if (this._overlay) return;
        const me = Game.activePlayer?.position;
        if (!me) return;
        const slot = trainingSlotAt(me.x, me.y);
        if (slot < 0) return;
        this._slot = slot;
        const origin = trainingSlotOrigin(slot);
        const px = (x: number, y: number): [number, number] => [(origin.x + x) * PIXI_SCALE, (origin.y + y) * PIXI_SCALE];
        const overlay = new Container();
        overlay.zIndex = ZIndexes.Ground + 0.5;
        const g = new Graphics();
        const ink = { color: 0xefe6d2, alpha: 0.55 };

        // the firing line: a dashed line across the lanes
        const top = TrainingLayout.laneY - 40, bottom = TrainingLayout.laneY + 40;
        for (let y = top; y < bottom; y += 4) {
            const [x1, y1] = px(TrainingLayout.firingLineX, y), [, y2] = px(TrainingLayout.firingLineX, Math.min(bottom, y + 2.2));
            g.moveTo(x1, y1).lineTo(x1, y2);
        }
        g.stroke({ width: 5, ...ink });

        // the main lane with a tick and a distance at every dummy
        const spots = trainingDummySpots();
        const far = Math.max(...spots.map(spot => spot.x));
        g.moveTo(...px(TrainingLayout.firingLineX, TrainingLayout.laneY)).lineTo(...px(far, TrainingLayout.laneY)).stroke({ width: 3, color: 0xefe6d2, alpha: 0.28 });
        const labels: Text[] = [];
        // (Arabic's and Hindi's letters drawn together, Arabic's words right to left: Pixi draws spaced letters one by
        // one, Arabic's then each on its own, unjoined, 1.04.5)
        const language = selectedLanguageCode();
        const words = (text: string): string => canvasLine(text, language);
        const label = (text: string, x: number, y: number, size: number, alpha = 0.85): void => {
            const mark = new Text({ text, style: { fontFamily: "TH-Oswald, Oswald, Arial Narrow, sans-serif", fontSize: size, fontWeight: "700", fill: 0xefe6d2, letterSpacing: spacedLetters(language) ? 2 : 0 } });
            mark.anchor.set(0.5);
            mark.alpha = alpha;
            mark.position.set(...px(x, y));
            labels.push(mark);
        };
        for (const distance of TrainingLayout.laneDistances) {
            const x = TrainingLayout.firingLineX + distance;
            g.moveTo(...px(x, TrainingLayout.laneY + 7)).lineTo(...px(x, TrainingLayout.laneY + 11)).stroke({ width: 4, ...ink });
            label(`${distance} m`, x, TrainingLayout.laneY + 15, 44);
        }
        label(words(t("FIRING LINE")), TrainingLayout.firingLineX, top - 5, 40, 0.7);
        label(words(t("ARMOURY · EVERY FARGO GUN")), TrainingLayout.rackX + 18, TrainingLayout.gunRackY - 7, 34, 0.8);
        const kitRows = Math.ceil(TrainingLoadout.kit.length / TrainingLayout.rackColumns);
        label(words(t("KIT · MELEE · THROWABLES · SCOPES · ARMOUR")), TrainingLayout.rackX + 18, TrainingLayout.kitRackY + kitRows * (TrainingLayout.rackSpacing + 1) + 1, 30, 0.8);
        // the practice yard for the Gravity Gloves (they're on the kit rack): a label above its barrels and boxes
        const yard = TrainingLayout.gravityYard;
        if (yard.length) {
            const left = Math.min(...yard.map(spot => spot.x)), right = Math.max(...yard.map(spot => spot.x));
            label(words(t("GRAVITY GLOVES YARD · GRAB · SWING · MERGE (V) · THROW")), (left + right) / 2, Math.min(...yard.map(spot => spot.y)) - 9, 30, 0.8);
        }

        overlay.addChild(g, ...labels);
        CameraManager.getContainer(Layer.Ground).addChild(overlay);
        this._overlay = overlay;
    }
}

export const TrainingHud = new TrainingHudClass();
